// NewsPage.js - Adam International Academy Documents Version
import { useState, useEffect, useRef } from 'react';
import './NewsPage.css';
import { Link } from 'react-router-dom';

// Import images (placeholder olarak kullanılacak, gerçek projede gerçek görseller eklenmeli)
import news1 from '../../assets/news/news1.jpg';
import news2 from '../../assets/news/news2.jpg';
import news3 from '../../assets/news/news3.jpg';
import event1 from '../../assets/news/event1.jpg';
import event2 from '../../assets/news/event2.jpg';
import event3 from '../../assets/news/event3.jpg';
import publication1 from '../../assets/news/publication1.jpg';

function NewsPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // News data from documents
  const newsData = [
    {
      id: 1,
      type: 'news',
      title: "Adam International Academy Launches New SDG-Aligned Programs",
      date: "March 15, 2026",
      description: "AIA introduces 8 new capacity development programs aligned with Sustainable Development Goals (SDG 2030), focusing on renewable energy, AI, and sustainable development.",
      image: news1,
      content: "Adam International Academy is proud to announce the launch of 8 new programs designed to strengthen technical capacities and contribute to the SDG Agenda 2030. The new programs include specialized courses in Renewable Energy Engineering, Artificial Intelligence for Sustainable Development, Data Science & Cybersecurity, and Corporate Sustainability. All programs are peer-reviewed by international experts and available in multiple languages including English, Arabic, and Turkish.",
      category: "Academic",
      link: "/programs"
    },
    {
      id: 2,
      type: 'news',
      title: "Strategic Partnership with United Nations Development Programme (UNDP)",
      date: "February 28, 2026",
      description: "AIA signs collaboration agreement with UNDP to develop joint capacity development initiatives for sustainable development professionals across Africa and Asia.",
      image: news2,
      content: "Adam International Academy has entered into a strategic partnership with the United Nations Development Programme (UNDP) to design and deliver capacity development interventions targeting policymakers and development professionals. The partnership will focus on multilingual e-learning courses, technical webinars, and blended learning programs that support the implementation of SDG 2030 in developing regions. This collaboration reinforces AIA's commitment to working collaboratively with United Nations agencies and development organizations.",
      category: "Partnership",
      link: "/partners"
    },
    {
      id: 3,
      type: 'news',
      title: "AIA Faculty Member Prof. Dr. Abdulnasir Munir Receives International Award",
      date: "February 10, 2026",
      description: "Professor Abdulnasir Munir honored with Excellence in Economic Development Award for his contributions to policy analysis and sustainable development research.",
      image: news3,
      content: "Prof. Dr. Abdulnasir Munir, a distinguished faculty member at Adam International Academy, has been awarded the prestigious Excellence in Economic Development Award by the International Economic Association. Professor Munir's research on economic feasibility studies and SDG implementation has been widely recognized for its impact on policy formulation in developing countries. He joins our faculty of 100+ high-level professionals from 30 countries dedicated to excellence in education and research.",
      category: "Achievement"
    }
  ];

  // Events data from documents
  const events = [
    {
      id: 4,
      type: 'event',
      title: "International Conference on Sustainable Development 2026",
      date: "June 15-17, 2026",
      time: "09:00-18:00",
      location: "Istanbul, Turkey & Online",
      description: "Join global leaders, policymakers, and academics for the 3rd Annual International Conference on Sustainable Development, focusing on SDG implementation strategies and capacity development.",
      image: event1,
      content: "The International Conference on Sustainable Development 2026 brings together experts from United Nations agencies, development organizations, academic institutions, and the private sector. The conference will feature keynote speeches, panel discussions, and workshops on topics including: SDG 4 (Quality Education), multilingual education strategies, innovation in capacity development, and public-private partnerships for sustainable development. Participants will have the opportunity to network with professionals from 30+ countries and explore collaboration opportunities.",
      category: "Conference",
      registrationLink: "/register"
    },
    {
      id: 5,
      type: 'event',
      title: "Multilingual Education Symposium",
      date: "May 20, 2026",
      time: "14:00-17:00",
      location: "Online (Zoom)",
      description: "Explore best practices in multilingual e-learning and culturally sensitive capacity development with international experts.",
      image: event2,
      content: "The Multilingual Education Symposium brings together educators, instructional designers, and language specialists to discuss innovative approaches to delivering education in multiple languages. Topics include: content adaptation to different language environments, culturally sensitive instructional design, and technologies for multilingual e-learning. The symposium is part of AIA's commitment to promoting multilingualism and accessible education for all.",
      category: "Symposium",
      registrationLink: "/register"
    },
    {
      id: 6,
      type: 'event',
      title: "Project Management Professional (PMP) Certification Workshop",
      date: "April 5-9, 2026",
      time: "09:30-16:30",
      location: "Bahrain & Online",
      description: "Intensive 5-day workshop preparing professionals for PMP certification with focus on international development projects.",
      image: event3,
      content: "This intensive workshop is designed for project managers seeking PMP certification and professionals working on international development projects. The curriculum covers project management frameworks, risk management, stakeholder engagement, and agile methodologies. Participants will receive comprehensive preparation materials and practice exams. The workshop is delivered by certified PMP instructors with extensive experience in development projects across Africa and the Middle East.",
      category: "Training"
    }
  ];

  // Press mentions and publications from documents
  const press = [
    {
      id: 7,
      type: 'press',
      title: "Adam International Academy: Bridging Education and Sustainable Development",
      source: "Forbes Middle East",
      date: "March 5, 2026",
      description: "Feature article on AIA's innovative approach to capacity development and multilingual education.",
      link: "#",
      category: "Magazine"
    },
    {
      id: 8,
      type: 'press',
      title: "Turkish Academy Expands Global Reach with African Partnerships",
      source: "Anadolu Agency",
      date: "February 15, 2026",
      description: "Coverage of AIA's expansion into African markets and partnerships with local universities.",
      link: "#",
      category: "News Agency"
    },
    {
      id: 9,
      type: 'press',
      title: "Launch of Adam International Journal: Peer-Reviewed Quarterly Publication",
      source: "Academic Journals Database",
      date: "January 20, 2026",
      description: "Announcement of AIA's new scientific journal focusing on sustainable development and capacity building research.",
      link: "#",
      category: "Publication"
    }
  ];

  // Publications from Adam Center
  const publications = [
    {
      id: 10,
      type: 'publication',
      title: "Adam International Journal - Volume 1, Issue 1",
      source: "Adam Center for Research and Studies",
      date: "January 2026",
      description: "Inaugural issue featuring research on sustainable development, economic policy, and educational innovation.",
      image: publication1,
      category: "Journal",
      downloadLink: "/downloads/journal-v1-i1"
    },
    {
      id: 11,
      type: 'publication',
      title: "Annual Report 2025: Impact and Achievements",
      source: "Adam International Academy",
      date: "February 2026",
      description: "Comprehensive report on AIA's activities, partnerships, and contributions to SDG 2030.",
      category: "Report",
      downloadLink: "/downloads/annual-report-2025"
    },
    {
      id: 12,
      type: 'publication',
      title: "Capacity Development Best Practices: A Guide for Practitioners",
      source: "Adam Center for Consulting",
      date: "March 2026",
      description: "Practical guide for designing and delivering effective capacity development interventions.",
      category: "Guide",
      downloadLink: "/downloads/capacity-development-guide"
    }
  ];

  const allItems = [...newsData, ...events, ...press, ...publications];

  const filteredItems = allItems.filter(item => {
    const matchesTab = activeTab === 'all' || item.type === activeTab;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (item.source && item.source.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const openModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 5000);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const getIconForType = (type) => {
    switch(type) {
      case 'news': return '📰';
      case 'event': return '📅';
      case 'press': return '📰';
      case 'publication': return '📚';
      default: return '📌';
    }
  };

  return (
    <div className="ap-wrapper">
      {/* Hero Section - Updated */}
      <section className="ap-hero">
        <div className="ap-hero-overlay">
          <div className="ap-hero-content">
            <h1>NEWS & EVENTS</h1>
            <p>Stay updated with the latest news, events, and publications from Adam International Academy</p>
          </div>
        </div>
      </section>

      {/* Filtering and Search */}
      <section 
        className="ap-section"
        ref={el => sectionRefs.current.filter = el}
        data-section="filter"
      >
        <div className={`ap-container ${visibleSections.filter ? 'visible' : ''}`}>
          <div className="ap-news-filter">
            <div className="ap-search-box">
              <input
                type="text"
                placeholder="Search news, events, publications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ap-search-input"
                aria-label="Search news and events"
              />
              <span className="ap-search-icon">🔍</span>
            </div>
            
            <div className="ap-filter-tabs">
              <button
                className={`ap-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
                aria-label="Show all content"
              >
                All ({allItems.length})
              </button>
              <button
                className={`ap-tab ${activeTab === 'news' ? 'active' : ''}`}
                onClick={() => setActiveTab('news')}
                aria-label="Show news only"
              >
                📰 News ({newsData.length})
              </button>
              <button
                className={`ap-tab ${activeTab === 'event' ? 'active' : ''}`}
                onClick={() => setActiveTab('event')}
                aria-label="Show events only"
              >
                📅 Events ({events.length})
              </button>
              <button
                className={`ap-tab ${activeTab === 'press' ? 'active' : ''}`}
                onClick={() => setActiveTab('press')}
                aria-label="Show press mentions"
              >
                📰 Press ({press.length})
              </button>
              <button
                className={`ap-tab ${activeTab === 'publication' ? 'active' : ''}`}
                onClick={() => setActiveTab('publication')}
                aria-label="Show publications"
              >
                📚 Publications ({publications.length})
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Content Grid */}
      <section 
        className="ap-section ap-news-section"
        ref={el => sectionRefs.current.news = el}
        data-section="news"
      >
        <div className={`ap-container ${visibleSections.news ? 'visible' : ''}`}>
          {filteredItems.length > 0 ? (
            <div className="ap-news-grid">
              {filteredItems.map((item) => (
                <div 
                  className={`ap-news-card ap-${item.type}-card`} 
                  key={item.id}
                  onClick={() => openModal(item)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && openModal(item)}
                >
                  {item.image && (
                    <div className="ap-news-image">
                      <img src={item.image} alt={item.title} loading="lazy" />
                      <div className="ap-news-category">
                        <span>{getIconForType(item.type)}</span>
                        <span>{item.category || item.type}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="ap-news-content">
                    <h3 className="ap-news-title">{item.title}</h3>
                    
                    <div className="ap-news-meta">
                      <span className="ap-news-date">📅 {item.date}</span>
                      {item.time && <span className="ap-news-time">⏰ {item.time}</span>}
                      {item.location && <span className="ap-news-location">📍 {item.location}</span>}
                      {item.source && <span className="ap-news-source">📰 {item.source}</span>}
                    </div>
                    
                    {item.description && (
                      <p className="ap-news-description">{item.description}</p>
                    )}
                    
                    <button className="ap-news-readmore">Read More →</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ap-no-results">
              <h3>No content matching your search</h3>
              <p>Please try a different search term or change filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events Highlight - New Section */}
      <section 
        className="ap-section ap-events-highlight"
        ref={el => sectionRefs.current.events = el}
        data-section="events"
      >
        <div className={`ap-container ${visibleSections.events ? 'visible' : ''}`}>
          <h2 className="ap-section-title">📅 Upcoming Events</h2>
          <div className="ap-events-list">
            {events.slice(0, 3).map((event) => (
              <div key={event.id} className="ap-event-highlight-card" onClick={() => openModal(event)}>
                <div className="ap-event-date-badge">
                  <span className="ap-event-month">{event.date.split(' ')[0]}</span>
                  <span className="ap-event-day">{event.date.split(' ')[1].replace(',', '')}</span>
                </div>
                <div className="ap-event-details">
                  <h3>{event.title}</h3>
                  <p>{event.time} | {event.location}</p>
                </div>
              </div>
            ))}
            <div className="ap-events-cta">
              <Link to="/events" className="ap-btn-secondary">View All Events →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section 
        className="ap-section ap-newsletter-section"
        ref={el => sectionRefs.current.newsletter = el}
        data-section="newsletter"
      >
        <div className={`ap-container ${visibleSections.newsletter ? 'visible' : ''}`}>
          <div className="ap-newsletter">
            <h2 className="ap-section-title">📬 Stay Updated</h2>
            <p className="ap-newsletter-text">
              Subscribe to our newsletter to receive the latest news, event invitations, and publication announcements directly in your inbox.
            </p>
            
            {subscribed ? (
              <div className="ap-newsletter-success">
                <div className="ap-success-icon">✓</div>
                <div className="ap-success-message">
                  <h3>Subscription Confirmed!</h3>
                  <p>Thank you for subscribing to Adam International Academy newsletter.</p>
                </div>
              </div>
            ) : (
              <form className="ap-newsletter-form" onSubmit={handleSubscribe}>
                <div className="ap-newsletter-input-group">
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="ap-newsletter-input"
                    aria-label="Email address for newsletter"
                  />
                  <button type="submit" className="ap-btn-primary">
                    Subscribe
                  </button>
                </div>
                <p className="ap-newsletter-note">
                  Your email address is kept confidential. You can unsubscribe at any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Detail Modal - Updated */}
      {isModalOpen && selectedItem && (
        <div className="ap-modal-overlay" onClick={closeModal}>
          <div className="ap-modal-content ap-news-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ap-modal-close" onClick={closeModal} aria-label="Close modal">×</button>
            
            <div className="ap-modal-body">
              {selectedItem.image && (
                <div className="ap-modal-image">
                  <img src={selectedItem.image} alt={selectedItem.title} loading="lazy" />
                </div>
              )}
              
              <div className="ap-modal-info">
                <div className="ap-modal-type-badge">
                  {getIconForType(selectedItem.type)} {selectedItem.type.charAt(0).toUpperCase() + selectedItem.type.slice(1)}
                </div>
                <h2>{selectedItem.title}</h2>
                
                <div className="ap-modal-meta">
                  <span className="ap-modal-date">📅 {selectedItem.date}</span>
                  {selectedItem.time && <span className="ap-modal-time">⏰ {selectedItem.time}</span>}
                  {selectedItem.location && <span className="ap-modal-location">📍 {selectedItem.location}</span>}
                  {selectedItem.source && <span className="ap-modal-source">📰 {selectedItem.source}</span>}
                  {selectedItem.category && <span className="ap-modal-category">🏷️ {selectedItem.category}</span>}
                </div>
                
                {selectedItem.content ? (
                  <div className="ap-modal-content-text">
                    <p>{selectedItem.content}</p>
                  </div>
                ) : selectedItem.description ? (
                  <div className="ap-modal-content-text">
                    <p>{selectedItem.description}</p>
                  </div>
                ) : null}
                
                <div className="ap-modal-actions">
                  {selectedItem.link && (
                    <Link to={selectedItem.link} className="ap-btn-primary">
                      Learn More
                    </Link>
                  )}
                  {selectedItem.registrationLink && (
                    <Link to={selectedItem.registrationLink} className="ap-btn-primary">
                      Register Now
                    </Link>
                  )}
                  {selectedItem.downloadLink && (
                    <a href={selectedItem.downloadLink} className="ap-btn-primary" download>
                      Download
                    </a>
                  )}
                  <button onClick={closeModal} className="ap-btn-secondary">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsPage;